import {
  AccountPageContextProps,
  accountPageDefaultValues,
  useAccountPageContext
} from "@contexts/account"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import i18next from "@utils/localization"
import { createContext, useContext } from "react"
import type {
  Control,
  UseFormHandleSubmit,
  UseFormReset
} from "react-hook-form"
import * as yup from "yup"
export type PasswordFormData = {
  newPassword: string
  confirmPassword: string
}
type AccountPasswordPageContextProps = AccountPageContextProps & {
  control: Control<PasswordFormData>
  handleSubmit?: UseFormHandleSubmit<PasswordFormData>
  isValid?: boolean
  isDirty?: boolean
  reset?: UseFormReset<PasswordFormData>
}

const defaultValues: AccountPasswordPageContextProps = {
  ...accountPageDefaultValues,
  translate: i18next.t,
  control: {} as Control<PasswordFormData>
}

const AccountPasswordPageContext = createContext(defaultValues)

export function AccountPasswordPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const accountPageContext = useAccountPageContext()
  const { translate } = defaultValues
  const schema = yup.object({
    newPassword: yup
      .string()
      .min(6, translate("password_must_be_atleast_6_characters"))
      .required(translate("password_field_is_required")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword")], translate("password_do_not_match"))
      .required(translate("password_field_is_required"))
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useControlledForm<PasswordFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  return (
    <AccountPasswordPageContext.Provider
      value={{
        ...defaultValues,
        ...accountPageContext,
        control,
        handleSubmit,
        isValid,
        isDirty,
        reset
      }}
    >
      {props.children}
    </AccountPasswordPageContext.Provider>
  )
}

export function useAccountPasswordPageContext() {
  const context = useContext(AccountPasswordPageContext)
  if (context === undefined) {
    throw new Error("AccountPasswordPage Context Error")
  }
  return context
}
